/**
 * Chat V2 Service - Canonical API Client
 * 
 * Handles all communication with the v2 chat API.
 * Enforces idempotency and proper metadata capture.
 */

interface SendMessageRequest {
  family_id: string;
  conversation_id: string;
  message: string;
  idempotency_key: string;
  client_timestamp?: string;
}

interface SendMessageResponse {
  user_event: ChatEvent;
  assistant_event: ChatEvent;
  turn_id: string;
}

interface ChatEvent {
  event_id: string;
  idempotency_key: string;
  family_id: string;
  conversation_id: string;
  turn_id: string | null;
  event_type: string;
  content_text: string | null;
  actor_type: string;
  server_received_at: string;
  server_persisted_at: string;
  client_timestamp: string | null;
  tokens_used: number;
  cost: number;
  model_config_snapshot: any;
}

interface HistoryResponse {
  events: ChatEvent[];
  conversation_id: string;
  total_count: number;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  turn_id?: string;
}

export class ChatV2Service {
  private apiBase: string;

  constructor(apiBase: string) {
    this.apiBase = apiBase;
  }

  /**
   * Send a message and get assistant response
   */
  async sendMessage(
    familyId: string,
    conversationId: string,
    message: string,
    idempotencyKey?: string
  ): Promise<SendMessageResponse> {
    const idemKey = idempotencyKey || this.generateIdempotencyKey();
    
    const request: SendMessageRequest = {
      family_id: familyId,
      conversation_id: conversationId,
      message: message,
      idempotency_key: idemKey,
      client_timestamp: new Date().toISOString()
    };

    const response = await fetch(`${this.apiBase}/chat/v2/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Send a message and stream the assistant response
   */
  async sendMessageStream(
    familyId: string,
    conversationId: string,
    message: string,
    onChunk: (chunk: string) => void,
    idempotencyKey?: string
  ): Promise<void> {
    const idemKey = idempotencyKey || this.generateIdempotencyKey();
    
    const request: SendMessageRequest = {
      family_id: familyId,
      conversation_id: conversationId,
      message: message,
      idempotency_key: idemKey,
      client_timestamp: new Date().toISOString()
    };

    const response = await fetch(`${this.apiBase}/chat/v2/send/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = JSON.parse(line.slice(6));
          if (data.content) {
            onChunk(data.content);
          }
          if (data.is_final) {
            return;
          }
          if (data.error) {
            throw new Error(data.error);
          }
        }
      }
    }
  }

  /**
   * Get conversation history
   */
  async getHistory(
    familyId: string,
    conversationId?: string,
    limit: number = 100
  ): Promise<Message[]> {
    const convId = conversationId || familyId;
    const params = new URLSearchParams({
      family_id: familyId,
      conversation_id: convId,
      limit: limit.toString()
    });

    const response = await fetch(
      `${this.apiBase}/chat/v2/history?${params}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to load history: ${response.status}`);
    }

    const data: HistoryResponse = await response.json();
    
    // Convert events to messages for UI
    return this.eventsToMessages(data.events);
  }

  /**
   * Seed conversation with deterministic data (for testing)
   */
  async seedConversation(
    familyId: string,
    numTurns: number,
    seedKey: string
  ): Promise<void> {
    const response = await fetch(`${this.apiBase}/chat/v2/seed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        family_id: familyId,
        num_turns: numTurns,
        seed_key: seedKey,
        participants: ['Parent']
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to seed: ${response.status}`);
    }
  }

  /**
   * Convert events to UI messages
   * Filters to only user and assistant message events
   */
  private eventsToMessages(events: ChatEvent[]): Message[] {
    return events
      .filter(e => 
        e.event_type === 'user_message_created' || 
        e.event_type === 'assistant_message_created'
      )
      .map(e => ({
        id: e.event_id,
        role: e.actor_type === 'user' ? 'user' : 'assistant',
        text: e.content_text || '',
        timestamp: new Date(e.server_received_at),
        turn_id: e.turn_id || undefined
      }));
  }

  /**
   * Generate a unique idempotency key
   */
  private generateIdempotencyKey(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }
}
